import type {DateRange} from '@/api/models/dateTime/dateRange';
import type {BookingResourceUsageQueryOptions} from '@/api/queries/queries/booking/useBookingResourceUsageQuery';
import type {QueryKey} from '@tanstack/vue-query';
import {get} from '@vueuse/core';
import {computed, unref, type MaybeRef, type MaybeRefOrGetter} from 'vue';

export const bookingKey = 'booking';

export const resourceUsageKey = [bookingKey, 'resourceUsage'] as const;

export function bookingResourceUsageKey({
    distinctSelectedDates,
    currentView,
    userMeBookingForId,
    userId,
}: BookingResourceUsageQueryOptions): QueryKey {
    return [
        ...resourceUsageKey,
        bookingSpaceResourceKey({
            spaceId: computed(() => unref(currentView).id),
            selectedDates: distinctSelectedDates,
        }),
        userMeBookingForId,
        userId,
    ];
}

export const bookingSideBarKey = [bookingKey, 'sideBar'] as const;

/**
 * Part of a query key for queries that handle bookings for specific spaces and dateRanges
 * @returns
 */
export function bookingSpaceResourceKey({
    spaceId,
    selectedDates,
}: {
    spaceId: MaybeRefOrGetter<string | null | undefined>;
    selectedDates: MaybeRefOrGetter<readonly DateRange[] | undefined | null>;
}) {
    return {
        type: 'bookingSpaceResources',
        spaceId,
        selectedDates,
    };
}

// A permission list, not booking data, so it stays outside the booking prefix.
export const bookingForAllowedKey = ['user', 'bookingForAllowed'] as const;

export function roomLiveStatusQueryKey(roomId: MaybeRef<string | null>): QueryKey {
    return [bookingKey, 'roomLiveStatus', roomId];
}

export const bookingStateKey = 'bookingState';

export function bookingStateQueryKey({
    resourceId,
    selectedDates,
    userMeBookingForId,
}: {
    resourceId: MaybeRef<string>;
    selectedDates: MaybeRef<DateRange[]>;
    userMeBookingForId: MaybeRef<string | undefined>;
}): QueryKey {
    return [
        bookingKey,
        bookingStateKey,
        resourceId,
        computed(() => get(selectedDates).map(d => `${d.from.format()}|${d.to.format()}`)),
        userMeBookingForId,
    ];
}
